import { gql } from '@apollo/client'
import { authClient } from './client'
import { API_AUTH_URL, AUTH_TOKEN_KEY } from '../utils/constants'

interface Package {
  _id: string
  title: string
  description: string
  bannerUrl: string
  themeUrl: string
  createdAt: string
  isDraft: boolean
  status: string
  isActive: boolean
  priceDetails: Array<{
    period: string
    actualPrice: number
    price: number
    gst: number
    totalprice: number
  }>
  faqs: Array<{
    question: string
    answer: string
  }>
}

interface PackageDetails {
  _id: string
  title: string
  description: string
  bannerBase64?: string
  bannerUrl: string
  themeBase64?: string
  themeUrl: string
  price: number
  isDraft: boolean
  isActive: boolean
  status: string
  courseIds: string[]
  courseDetails: Array<{
    _id: string
    title: string
    description: string
    thumbnail: string
    language: string
    publishStatus: string
    createdAt: string
    desktopAvailable: boolean
  }>
  telegramId: string[]
  priceDetails: Array<{
    period: string
    actualPrice: number
    price: number
    gst: number
    totalprice: number
  }>
  faqs: Array<{
    question: string
    answer: string
  }>
  createdAt: string
  updatedAt: string
  createdBy: string
}

interface PackageResponse {
  status: number
  message: string
  data?: Package[]
}

interface PackageDetailResponse {
  status: number
  message: string
  data?: PackageDetails
}

interface PriceDetail {
  period: string
  actualPrice: number
  price: number
  gst: number
  totalprice: number
}

interface CreatePackageParams {
  title: string
  description: string
  bannerFile: File
  themeFile: File
  courseIds: string[]
  telegramId?: string[]
  priceDetails?: PriceDetail[]
  faqs?: Array<{question: string, answer: string}>
  isDraft?: boolean
  status?: string
}

interface CreatePackageResponse {
  status: number
  message: string
  data?: {
    _id: string
    title: string
    description: string
    bannerUrl: string
    themeUrl: string
    isDraft?: boolean
    status: string
    priceDetails: PriceDetail[]
    faqs?: Array<{question: string, answer: string}>
  } | null
}

interface UpdatePackageParams {
  packageId: string
  title?: string
  description?: string
  courseIds?: string[]
  telegramId?: string[]
  priceDetails?: PriceDetail[]
  faqs?: Array<{question: string, answer: string}>
  bannerFile?: File | null
  themeFile?: File | null
  status?: string
  isDraft?: boolean
}

interface UpdatePackageResponse {
  status: number
  message: string
  data?: {
    _id: string
    title: string
    description: string
    bannerUrl: string
    themeUrl: string
    status: string
    priceDetails: PriceDetail[]
    faqs?: Array<{question: string, answer: string}>
    updatedAt: string
    updatedBy: string
  } | null
}

interface DeletePackageResponse {
  status: number
  message: string
  data?: {
    _id: string
    title: string
    description: string
    bannerUrl: string
    themeUrl: string
    bannerBase64?: string
    themeBase64?: string
    isActive: boolean
    isDeleted: boolean
    isDraft: boolean
    status: string
    createdAt: string
    updatedAt: string
    createdBy: string
    updatedBy: string
    deletedAt: string
    deletedBy: string
    priceDetails: PriceDetail[]
    courseIds: string[]
    courseDetails: Array<{
      _id: string
      title: string
      description: string
      thumbnail: string
      hls: string
      language: string
      desktopAvailable: boolean
      createdBy: string
      creationStage: string
      publishStatus: string
      isDeleted: boolean
      deletedBy: string
      deletedAt: string
      createdAt: string
    }>
    telegramId: string[]
    faqs: Array<{
      question: string
      answer: string
    }>
  } | null
}

export const getAllPackages = async (): Promise<PackageResponse> => {
  try {
    const GET_ALL_PACKAGES = gql`
      query GetPackages {
        getPackages {
          _id
          title
          description
          bannerBase64
          bannerUrl
          themeBase64
          themeUrl
          createdAt
          isDraft
          status
          isActive
          priceDetails {
            period
            actualPrice
            price
            gst
            totalprice
          }
          faqs {
            question
            answer
          }
        }
      }
    `
    const { data } = await authClient.query({
      query: GET_ALL_PACKAGES,
      fetchPolicy: 'network-only',
    })
    return {
      status: 200,
      message: 'Success',
      data: data.getPackages,
    }
  } catch (error: any) {
    console.error('Error fetching packages:', error)
    return {
      status: 500,
      message: 'Failed to fetch packages',
    }
  }
}

export const getPackageById = async (packageId: string): Promise<PackageDetailResponse> => {
  try {
    const GET_PACKAGE_BY_ID = gql`
      query GetPackageById($packageId: String!) {
        getPackages(packageId: $packageId) {
          _id
          title
          description
          bannerBase64
          bannerUrl
          themeBase64
          themeUrl
          isDraft
          isActive
          status
          courseIds
          courseDetails {
            _id
            title
            description
            thumbnail
            language
            publishStatus
            createdAt
            desktopAvailable
            createdBy
            creationStage
            isDeleted
            deletedAt
          }
          telegramId
          priceDetails {
            period
            actualPrice
            price
            gst
            totalprice
          }
          faqs {
            question
            answer
          }
          createdAt
          updatedAt
          createdBy
          updatedBy
          isDeleted
          deletedAt
        }
      }
    `
    
    const { data } = await authClient.query({
      query: GET_PACKAGE_BY_ID,
      variables: { packageId },
      fetchPolicy: 'network-only',
    })
    
    if (data?.getPackages && data.getPackages.length > 0) {
      return {
        status: 200,
        message: 'Success',
        data: data.getPackages[0],
      }
    } else {
      return {
        status: 404,
        message: 'Package not found',
      }
    }
  } catch (error: any) {
    console.error('Error fetching package by ID:', error)
    return {
      status: 500,
      message: 'Failed to fetch package details',
    }
  }
}

export const createPackage = async (params: CreatePackageParams): Promise<CreatePackageResponse> => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY)
  if (!token) {
    return { status: 401, message: 'Authentication required', data: null }
  }
  
  const mutation = `
    mutation CreatePackage($title: String, $description: String, $isDraft: Boolean, $status: String, $courseIds: [String!], $telegramId: [String!], $priceDetails: [PriceInput!], $faqs: [FaqInput!], $bannerFile: Upload, $themeFile: Upload) {
      createPackage(title: $title, description: $description, isDraft: $isDraft, status: $status, courseIds: $courseIds, telegramId: $telegramId, priceDetails: $priceDetails, faqs: $faqs, bannerFile: $bannerFile, themeFile: $themeFile) {
        status
        message
        data {
          _id
          title
          description
          bannerUrl
          themeUrl
          isDraft
          status
          priceDetails {
            period
            actualPrice
            price
            gst
            totalprice
          }
          faqs {
            question
            answer
          }
        }
      }
    }
  `
  
  const operations = {
    query: mutation,
    variables: {
      title: params.title,
      description: params.description,
      isDraft: params.isDraft || false,
      status: params.status || "active",
      courseIds: params.courseIds || [],
      telegramId: params.telegramId || [],
      priceDetails: params.priceDetails || [],
      faqs: params.faqs || [],
      bannerFile: null,
      themeFile: null,
    },
  }
  
  const map = {
    '0': ['variables.bannerFile'],
    '1': ['variables.themeFile'],
  }
  
  const formData = new FormData()
  formData.append('operations', JSON.stringify(operations))
  formData.append('map', JSON.stringify(map))
  formData.append('0', params.bannerFile)
  formData.append('1', params.themeFile)
  
  try {
    const response = await fetch(API_AUTH_URL + '/graphql', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    })
    
    const result = await response.json()
    
    if (result.errors) {
      return { 
        status: 500, 
        message: result.errors[0].message, 
        data: null 
      }
    }
    
    if (!result.data?.createPackage) {
      return { 
        status: 500, 
        message: 'Failed to create package', 
        data: null 
      }
    }
    
    return result.data.createPackage
  } catch (error: any) {
    console.error('Error creating package:', error)
    return { 
      status: 500, 
      message: error.message || 'Failed to create package. Please try again.', 
      data: null 
    }
  }
}
export const updatePackage = async (params: UpdatePackageParams): Promise<UpdatePackageResponse> => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY)
  if (!token) {
    return { status: 401, message: 'Authentication required', data: null }
  }
  
  // If we have file uploads, use multipart form data
  if (params.bannerFile || params.themeFile) {
    const mutation = `
      mutation UpdatePackage(
        $packageId: String!
        $title: String
        $description: String
        $courseIds: [String!]
        $telegramId: [String!]
        $priceDetails: [PriceInput!]
        $faqs: [FaqInput!]
        $bannerFile: Upload
        $themeFile: Upload
        $status: String
        $isDraft: Boolean
      ) {
        updatePackage(
          packageId: $packageId
          title: $title
          description: $description
          courseIds: $courseIds
          telegramId: $telegramId
          priceDetails: $priceDetails
          faqs: $faqs
          bannerFile: $bannerFile
          themeFile: $themeFile
          status: $status
          isDraft: $isDraft
        ) {
          status
          message
          data {
            _id
            title
            description
            bannerUrl
            themeUrl
            status
            isDraft
            priceDetails {
              period
              actualPrice
              price
              gst
              totalprice
            }
            faqs {
              question
              answer
            }
            updatedAt
            updatedBy
          }
        }
      }
    `
    
    const operations: any = {
      query: mutation,
      variables: {
        packageId: params.packageId,
      },
    }
    
    // Only add fields that are being updated
    if (params.title !== undefined) operations.variables.title = params.title
    if (params.description !== undefined) operations.variables.description = params.description
    if (params.courseIds !== undefined) operations.variables.courseIds = params.courseIds
    if (params.telegramId !== undefined) operations.variables.telegramId = params.telegramId
    if (params.priceDetails !== undefined) operations.variables.priceDetails = params.priceDetails
    if (params.faqs !== undefined) {
      operations.variables.faqs = params.faqs.map(faq => ({
        question: faq.question,
        answer: faq.answer
      }))
    }
    if (params.bannerFile !== undefined) operations.variables.bannerFile = params.bannerFile
    if (params.themeFile !== undefined) operations.variables.themeFile = params.themeFile
    if (params.status !== undefined) operations.variables.status = params.status
    if (params.isDraft !== undefined) operations.variables.isDraft = params.isDraft
    
    const map: any = {}
    let fileIndex = 0
    
    if (params.bannerFile) {
      map[fileIndex.toString()] = ['variables.bannerFile']
      fileIndex++
    }
    
    if (params.themeFile) {
      map[fileIndex.toString()] = ['variables.themeFile']
    }
    
    const formData = new FormData()
    formData.append('operations', JSON.stringify(operations))
    formData.append('map', JSON.stringify(map))
    
    if (params.bannerFile) {
      formData.append('0', params.bannerFile)
    }
    
    if (params.themeFile) {
      formData.append(params.bannerFile ? '1' : '0', params.themeFile)
    }
    
    try {
      const response = await fetch(API_AUTH_URL + '/graphql', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      })
      
      const result = await response.json()
      
      if (result.errors) {
        return { 
          status: 500, 
          message: result.errors[0].message, 
          data: null 
        }
      }
      
      if (!result.data?.updatePackage) {
        return { 
          status: 500, 
          message: 'Failed to update package', 
          data: null 
        }
      }
      
      return result.data.updatePackage
    } catch (error: any) {
      console.error('Error updating package:', error)
      return { 
        status: 500, 
        message: error.message || 'Failed to update package. Please try again.', 
        data: null 
      }
    }
  } else {
    // For non-file updates, use GraphQL client
    try {
      const UPDATE_PACKAGE_MUTATION = gql`
        mutation UpdatePackage(
          $packageId: String!
          $title: String
          $description: String
          $courseIds: [String!]
          $telegramId: [String!]
          $priceDetails: [PriceInput!]
          $faqs: [FaqInput!]
          $status: String
          $isDraft: Boolean
        ) {
          updatePackage(
            packageId: $packageId
            title: $title
            description: $description
            courseIds: $courseIds
            telegramId: $telegramId
            priceDetails: $priceDetails
            faqs: $faqs
            status: $status
            isDraft: $isDraft
          ) {
            status
            message
            data {
              _id
              title
              description
              bannerUrl
              themeUrl
              status
              isDraft
              priceDetails {
                period
                actualPrice
                price
                gst
                totalprice
              }
              faqs {
                question
                answer
              }
              updatedAt
              updatedBy
            }
          }
        }
      `

      const variables: any = {
        packageId: params.packageId,
      }

      // Only add fields that are being updated
      if (params.title !== undefined) variables.title = params.title
      if (params.description !== undefined) variables.description = params.description
      if (params.courseIds !== undefined) variables.courseIds = params.courseIds
      if (params.telegramId !== undefined) variables.telegramId = params.telegramId
      if (params.priceDetails !== undefined) {
        variables.priceDetails = params.priceDetails
      }
      if (params.faqs !== undefined) {
        variables.faqs = params.faqs.map(faq => ({
          question: faq.question,
          answer: faq.answer
        }))
      }
      if (params.status !== undefined) variables.status = params.status
      if (params.isDraft !== undefined) variables.isDraft = params.isDraft

      // Only make API call if there are fields to update
      if (Object.keys(variables).length <= 1) { // Only packageId present
        return {
          status: 200,
          message: 'No changes to update',
        }
      }

      const { data } = await authClient.mutate({
        mutation: UPDATE_PACKAGE_MUTATION,
        variables,
      })

      return {
        status: data.updatePackage.status,
        message: data.updatePackage.message,
        data: data.updatePackage.data,
      }
    } catch (error: any) {
      console.error('Error updating package:', error)
      return {
        status: 500,
        message: error.message || 'Failed to update package',
        data: null,
      }
    }
  }
}
// Update the deletePackage function to use the proper delete mutation
export const deletePackage = async (packageId: string): Promise<DeletePackageResponse> => {
  try {
    const DELETE_PACKAGE_MUTATION = gql`
      mutation DeletePackage($packageId: String!) {
        deletePackage(packageId: $packageId) {
          status
          message
          data {
            _id
            title
            description
            bannerUrl
            themeUrl
            bannerBase64
            themeBase64
            isActive
            isDeleted
            isDraft
            status
            createdAt
            updatedAt
            createdBy
            updatedBy
            deletedAt
            deletedBy
            priceDetails {
              period
              actualPrice
              price
              gst
              totalprice
            }
            courseIds
            courseDetails {
              _id
              title
              description
              thumbnail
              hls
              language
              desktopAvailable
              createdBy
              creationStage
              publishStatus
              isDeleted
              deletedBy
              deletedAt
              createdAt
            }
            telegramId
            faqs {
              question
              answer
            }
          }
        }
      }
    `

    const { data } = await authClient.mutate({
      mutation: DELETE_PACKAGE_MUTATION,
      variables: { packageId },
    })

    return {
      status: data.deletePackage.status,
      message: data.deletePackage.message,
      data: data.deletePackage.data,
    }
  } catch (error: any) {
    console.error('Error deleting package:', error)
    return {
      status: 500,
      message: error.message || 'Failed to delete package',
      data: null,
    }
  }
}

interface ApiResponse {
  status: number
  message: string
  data?: any
}

export const softDeleteCourse = async (id: string, deletedBy: string): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_AUTH_URL}/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}`
      },
      body: JSON.stringify({
        query: `
          mutation softDeleteCourse($id: String!, $deletedBy: String) {
            softDeleteCourse(
              id: $id, 
              deletedBy: $deletedBy
            ) {
              status
              message
              data {
                _id
                title
                isDeleted
              }
            }
          }
        `,
        variables: { id, deletedBy }
      })
    });
    const result = await response.json();
    return result.data?.softDeleteCourse || {
      status: 500,
      message: 'Failed to delete course'
    };
  } catch (error: any) {
    console.error('Error deleting course:', error);
    return {
      status: 500,
      message: error.message || 'Error deleting course'
    };
  }
};

// Purchase API functions
interface CoursePurchase {
  courseId: string;
  courseViewPercent: number;
}

interface CreatePurchaseParams {
  userId: string;
  name: string;
  email: string;
  phone: string;
  packageId?: string;
  courses?: CoursePurchase[];
}

interface UpdateCourseProgressParams {
  purchaseId: string;
  courseId: string;
  viewPercent: number;
}

interface PurchaseResponse {
  status: number;
  message: string;
  data?: any;
}
// export const createPurchase = async (purchaseData: CreatePurchaseParams): Promise<PurchaseResponse> => {
//   try {
//     const CREATE_PURCHASE_MUTATION = gql`
//       mutation CreatePurchase(
//         $userId: String!
//         $name: String!
//         $email: String!
//         $phone: String!
//         $packageId: String
//         $courses: [CourseProgressInput!]!
//       ) {
//         createPurchase(
//           userId: $userId
//           name: $name
//           email: $email
//           phone: $phone
//           packageId: $packageId
//           courses: $courses
//         )
//       }
//     `;

//     const variables: any = {
//       userId: purchaseData.userId,
//       name: purchaseData.name,
//       email: purchaseData.email,
//       phone: purchaseData.phone,
//       packageId: purchaseData.packageId,
//     };
    
//     // Only include courses if it's provided (even if empty)
//     if (purchaseData.courses !== undefined) {
//       variables.courses = purchaseData.courses && purchaseData.courses.length > 0 
//         ? purchaseData.courses 
//         : [];
//     }

//     const { data } = await authClient.mutate({
//       mutation: CREATE_PURCHASE_MUTATION,
//       variables,
//     });

//     if (data.createPurchase) {
//       return {
//         status: 200,
//         message: 'Purchase created successfully',
//         data: {
//           purchaseId: data.createPurchase
//         }
//       };
//     } else {
//       return {
//         status: 500,
//         message: 'Failed to create purchase',
//         data: null
//       };
//     }
//   } catch (error: any) {
//     console.error('Error creating purchase:', error);
//     return {
//       status: 500,
//       message: error.message || 'Failed to create purchase',
//       data: null,
//     };
//   }
// };



export const createPurchase = async (purchaseData: CreatePurchaseParams): Promise<PurchaseResponse> => {
  try {
    const CREATE_PURCHASE_MUTATION = gql`
      mutation CreatePurchase(
        $user_id: String!
        $name: String!
        $email: String!
        $phone: String!
        $package_id: String
        $courses: [CourseProgressInput!]!
      ) {
        createPurchase(
          user_id: $user_id
          name: $name
          email: $email
          phone: $phone
          package_id: $package_id
          courses: $courses
        )
      }
    `;

    const variables: any = {
      user_id: purchaseData.userId,
      name: purchaseData.name,
      email: purchaseData.email,
      phone: purchaseData.phone,
      package_id: purchaseData.packageId,
      courses: purchaseData.courses?.length
        ? purchaseData.courses.map(course => ({
            course_id: course.courseId,
            course_view_percent: course.courseViewPercent,
          }))
        : [],
    };

    const { data } = await authClient.mutate({
      mutation: CREATE_PURCHASE_MUTATION,
      variables,
    });

    if (data?.createPurchase) {
      return {
        status: 200,
        message: 'Purchase created successfully',
        data: {
          purchaseId: data.createPurchase,
        },
      };
    }

    return {
      status: 500,
      message: 'Failed to create purchase',
      data: null,
    };

  } catch (error: any) {
    console.error('Error creating purchase:', error);
    return {
      status: 500,
      message: error.message || 'Failed to create purchase',
      data: null,
    };
  }
};


// Add interface for progress update
export interface ProgressUpdateParams {
  purchaseId: string;
  courseId: string;
  viewPercent: number;
}

// Add function to update course progress
export const updateCourseProgress = async (progressData: ProgressUpdateParams): Promise<{status: number; message: string; data?: any}> => {
  try {
    const UPDATE_COURSE_PROGRESS = gql`
      mutation UpdateCourseProgress($purchaseId: String!, $courseId: String!, $viewPercent: Float!) {
        updateCourseProgress(
          purchaseId: $purchaseId, 
          courseId: $courseId, 
          viewPercent: $viewPercent
        )
      }
    `;

    const { data } = await authClient.mutate({
      mutation: UPDATE_COURSE_PROGRESS,
      variables: progressData,
    });

    if (data?.updateCourseProgress) {
      return {
        status: 200,
        message: 'Progress updated successfully',
        data: data.updateCourseProgress,
      };
    } else {
      return {
        status: 500,
        message: 'Failed to update course progress',
      };
    }
  } catch (error: any) {
    console.error('Error updating course progress:', error);
    return {
      status: 500,
      message: error.message || 'Failed to update course progress',
    };
  }
};

// Export the interface for use in other components
export type { UpdateCourseProgressParams };

// Add the new interface for purchase data
export interface PurchaseData {
  __typename: string;
  userId: string;
  purchases: Array<{
    purchaseId: string;
    createdAt: string;
    packageId: string | null;
    courses: Array<{
      courseId: string;
      courseViewPercent: number;
      certificateSent: boolean;
    }>;
  }>;
}


// Add the new function to fetch purchase data
export const getPurchaseData = async (userId: string): Promise<{status: number; message: string; data?: PurchaseData}> => {
  try {
    const GET_PURCHASE_DATA = gql`
      query GetPurchaseData($userId: String!) {
        getPurchaseData(filter: {userId: $userId}) {
          ... on UserPurchaseOutput {
            __typename
            userId
            purchases {
              purchaseId
              createdAt
              packageId
              courses {
                certificateSent
                courseId
                courseViewPercent
              }
            }
          }
        }
      }
    `;

    const { data } = await authClient.query({
      query: GET_PURCHASE_DATA,
      variables: { userId },
      fetchPolicy: 'network-only',
    });

    if (data?.getPurchaseData) {
      return {
        status: 200,
        message: 'Success',
        data: data.getPurchaseData,
      };
    } else {
      return {
        status: 404,
        message: 'No purchase data found',
      };
    }
  } catch (error: any) {
    console.error('Error fetching purchase data:', error);
    return {
      status: 500,
      message: error.message || 'Failed to fetch purchase data',
    };
  }
};

// Add the new function to initialize course progress for single course purchases
export const initializeCourseProgress = async (userId: string, courseId: string, expiry: string): Promise<{status: number; message: string; data?: any}> => {
  try {
    // Make API call using fetch instead of GraphQL client to match the curl command format
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      return {
        status: 401,
        message: 'Authentication required',
      };
    }

    const response = await fetch(API_AUTH_URL + '/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          mutation InitializeCourseProgress($userId: String!, $courseId: String!, $expiry: String!) {
            initializeCourseProgress(userId: $userId, courseId: $courseId, expiry: $expiry) {
              userId
              courseId
            }
          }
        `,
        variables: {
          userId,
          courseId,
          expiry
        }
      })
    });

    const result = await response.json();
    
    if (result.errors && result.errors.length > 0) {
      console.error("GraphQL errors:", result.errors);
      return {
        status: 400,
        message: result.errors[0].message || "GraphQL error occurred"
      };
    }
    
    if (result.data?.initializeCourseProgress) {
      return {
        status: 200,
        message: 'Course progress initialized successfully',
        data: result.data.initializeCourseProgress
      };
    } else {
      return {
        status: 500,
        message: 'Failed to initialize course progress'
      };
    }
  } catch (error: any) {
    console.error('Error initializing course progress:', error);
    return {
      status: 500,
      message: error.message || 'Failed to initialize course progress'
    };
  }
};

// Add the new function to initialize package progress for package purchases
export const initializePackageProgress = async (userId: string, packageId: string): Promise<{status: number; message: string; data?: any}> => {
  try {
    // Make API call using fetch instead of GraphQL client to match the curl command format
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      return {
        status: 401,
        message: 'Authentication required',
      };
    }

    const response = await fetch(API_AUTH_URL + '/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          mutation MyMutation { 
            initializeCourseProgress(
              userId: "${userId}", 
              packageId: "${packageId}"
            ) { 
              _id 
              courseDuration 
              courseId 
              createdAt 
              daysLeft 
              expiry 
              lessonDuration 
              lessonIds 
              lessonProgress { 
                lessonId 
                progressPercent 
                watchTime 
              } 
              packageId 
              totalProgressPercent 
              totalWatchTime 
              updatedAt 
              userId 
              watchTimes { 
                lessonId 
                watchTime 
              } 
            } 
          }
        `
      })
    });

    const result = await response.json();
    
    if (result.errors && result.errors.length > 0) {
      console.error("GraphQL errors:", result.errors);
      return {
        status: 400,
        message: result.errors[0].message || "GraphQL error occurred"
      };
    }
    
    if (result.data?.initializeCourseProgress) {
      return {
        status: 200,
        message: 'Package progress initialized successfully',
        data: result.data.initializeCourseProgress
      };
    } else {
      return {
        status: 500,
        message: 'Failed to initialize package progress'
      };
    }
  } catch (error: any) {
    console.error('Error initializing package progress:', error);
    return {
      status: 500,
      message: error.message || 'Failed to initialize package progress'
    };
  }
};

// Add the new function to update lesson watch time
export const updateLessonWatchTime = async (userId: string, courseId: string, lessonId: string, newWatchTimeSeconds: number): Promise<{status: number; message: string; data?: any}> => {
  try {
    // Make API call using fetch instead of GraphQL client to match the curl command format
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      return {
        status: 401,
        message: 'Authentication required',
      };
    }

    // Create the GraphQL query string with variables
    const query = `
      mutation MyMutation { 
        updateLessonWatchTime(
          data: {
            userId: "${userId}", 
            courseId: "${courseId}", 
            lessonId: "${lessonId}", 
            newWatchTimeSeconds: ${newWatchTimeSeconds}
          }
        ) { 
          message 
          success 
        } 
      }
    `;

    console.log('Sending GraphQL request:', { query, userId, courseId, lessonId, newWatchTimeSeconds });

    const response = await fetch(API_AUTH_URL + '/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        query
      })
    });

    const result = await response.json();
    
    console.log('Received GraphQL response:', result);
    
    if (result.errors && result.errors.length > 0) {
      console.error("GraphQL errors:", result.errors);
      return {
        status: 400,
        message: result.errors[0].message || "GraphQL error occurred"
      };
    }
    
    if (result.data?.updateLessonWatchTime) {
      return {
        status: 200,
        message: 'Lesson watch time updated successfully',
        data: result.data.updateLessonWatchTime
      };
    } else {
      return {
        status: 500,
        message: 'Failed to update lesson watch time'
      };
    }
  } catch (error: any) {
    console.error('Error updating lesson watch time:', error);
    return {
      status: 500,
      message: error.message || 'Failed to update lesson watch time'
    };
  }
};

// Add the new function to fetch course progress
export const getCourseProgress = async (courseId: string, userId: string): Promise<{status: number; message: string; data?: any}> => {
  try {
    // Make API call using fetch instead of GraphQL client to match the curl command format
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      return {
        status: 401,
        message: 'Authentication required',
      };
    }

    const response = await fetch(API_AUTH_URL + '/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          query MyQuery { 
            getCourseProgress(
              courseId: "${courseId}", 
              userId: "${userId}"
            ) { 
              _id 
              courseDuration 
              courseId 
              createdAt 
              daysLeft 
              expiry 
              lessonDuration 
              lessonIds 
              lessonProgress { 
                lessonId 
                progressPercent 
                watchTime 
              } 
              packageId 
              totalProgressPercent 
              totalWatchTime 
              updatedAt 
              userId 
              watchTimes { 
                lessonId 
                watchTime 
              } 
            } 
          }
        `
      })
    });

    const result = await response.json();
    
    if (result.errors && result.errors.length > 0) {
      console.error("GraphQL errors:", result.errors);
      return {
        status: 400,
        message: result.errors[0].message || "GraphQL error occurred"
      };
    }
    
    if (result.data?.getCourseProgress) {
      return {
        status: 200,
        message: 'Course progress fetched successfully',
        data: result.data
      };
    } else {
      return {
        status: 500,
        message: 'Failed to fetch course progress'
      };
    }
  } catch (error: any) {
    console.error('Error fetching course progress:', error);
    return {
      status: 500,
      message: error.message || 'Failed to fetch course progress'
    };
  }
};

export interface WatchHistoryItem {
  courseId: string;
  courseTitle: string;
  courseType: string;
  thumbnail: string | null;
  progressPercent: number;
  totalWatchTime: number;
  startedAt: string | null;
  lastWatchedAt: string | null;
}

export const getUserWatchHistory = async (userId: string): Promise<{ status: number; message: string; data?: WatchHistoryItem[] }> => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return { status: 401, message: 'Authentication required' };

    const response = await fetch(API_AUTH_URL + '/graphql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        query: `
          query GetUserWatchHistory($userId: String!) {
            getUserWatchHistory(userId: $userId) {
              status
              message
              data {
                courseId
                courseTitle
                courseType
                thumbnail
                progressPercent
                totalWatchTime
                startedAt
                lastWatchedAt
              }
            }
          }
        `,
        variables: { userId },
      }),
    });

    const result = await response.json();
    if (result.errors?.length) return { status: 400, message: result.errors[0].message };
    if (result.data?.getUserWatchHistory) return result.data.getUserWatchHistory;
    return { status: 500, message: 'Failed to fetch watch history' };
  } catch (error: any) {
    return { status: 500, message: error.message || 'Failed to fetch watch history' };
  }
};

export interface AllWatchHistoryItem {
  userId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  courseId: string;
  courseTitle: string;
  courseType: string;
  thumbnail: string | null;
  progressPercent: number;
  totalWatchTime: number;
  startedAt: string | null;
  lastWatchedAt: string | null;
}

export const getAllWatchHistory = async (courseType?: string): Promise<{ status: number; message: string; data?: AllWatchHistoryItem[] }> => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return { status: 401, message: 'Authentication required' };

    const response = await fetch(API_AUTH_URL + '/graphql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        query: `
          query GetAllWatchHistory($courseType: String) {
            getAllWatchHistory(courseType: $courseType) {
              status
              message
              data {
                userId
                userName
                userEmail
                userPhone
                courseId
                courseTitle
                courseType
                thumbnail
                progressPercent
                totalWatchTime
                startedAt
                lastWatchedAt
              }
            }
          }
        `,
        variables: { courseType: courseType || null },
      }),
    });

    const result = await response.json();
    if (result.errors?.length) return { status: 400, message: result.errors[0].message };
    if (result.data?.getAllWatchHistory) return result.data.getAllWatchHistory;
    return { status: 500, message: 'Failed to fetch watch history' };
  } catch (error: any) {
    return { status: 500, message: error.message || 'Failed to fetch watch history' };
  }
};

// Add interface for purchased course details
export interface PurchasedCourse {
  _id: string;
  title: string;
  description: string;
  thumbnail: string;
  createdAt: string;
  language: string;
  desktopAvailable: boolean;
  PublishStatus: string;
  courseViewPercent: number;
  certificateSent: boolean;
  status: "yet_to_start" | "in_progress" | "completed";
  modules?: string;
  moduleCount?: number;
  lessonCount?: number;
}

// Add interface for purchased course with detailed modules
export interface PurchasedCourseWithDetails {
  _id: string;
  title: string;
  description: string;
  thumbnail: string;
  createdAt: string;
  language: string;
  desktopAvailable: boolean;
  PublishStatus: string;
  courseViewPercent: number;
  certificateSent: boolean;
  status: "yet_to_start" | "in_progress" | "completed";
  modules: Module[];
  moduleCount?: number;
  lessonCount?: number;
}

// Add interface for module
export interface Module {
  _id: string;
  title: string;
  description: string;
  lessons: Lesson[];
}

// Add interface for lesson
export interface Lesson {
  _id: string;
  title: string;
  description: string;
  lessonType: string;
  duration: number;
}

// Add interface for purchased package
export interface PurchasedPackage {
  packageId: string | null;
  packageName: string;
  packageThumbnail: string;
  createdAt: string;
  courses: PurchasedCourseWithDetails[];
}


interface CoursePurchase {
  courseId: string;
  courseViewPercent: number;
}

interface CreatePurchaseParams {
  userId: string;
  name: string;
  email: string;
  phone: string;
  packageId?: string;
  courses?: CoursePurchase[];
}

interface UpdateCourseProgressParams {
  purchaseId: string;
  courseId: string;
  viewPercent: number;
}

interface PurchaseResponse {
  status: number;
  message: string;
  data?: any;
}

// User Service Integration - Move user service functionality here

// GraphQL query to fetch user counts (total, active, deleted)
export const GET_USER_COUNTS = gql`
  query MyQuery {
    allUsers {
      totalCount
      activeCount
      deletedCount
    }
  }
`;

// GraphQL query to fetch all users (active + inactive + deleted)
export const GET_ALL_USERS = gql`
  query MyQuery {
    allUsers {
      users {
        _id
        createdAt
        email
        isActive
        isDeleted
        name
        usertype
        usertypeId
        phone
        companyId
        companyName
        childCompanyId
        childCompanyName
        department
        departmentName
        city
        state
        country
        gender
      }
    }
  }
`;

// GraphQL query to fetch only active users
export const GET_ACTIVE_USERS = gql`
  query MyQuery {
    allUsers(active: true) {
      users {
        _id
        createdAt
        email
        isActive
        isDeleted
        name
        phone
        usertype
        usertypeId
        companyId
        companyName
        childCompanyId
        childCompanyName
        department
        departmentName
        city
        state
        country
        gender
      }
    }
  }
`;

// Function to fetch user counts
export const fetchUserCounts = async () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/admin/user-counts`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching user counts:', error);
    throw error;
  }
};

// Function to fetch all users
export const fetchAllUsers = async () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/admin/admins`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Return data in the same format as expected by the dashboard
    return {
      users: result.data || result
    };
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Function to create a new user
export const createUser = async (userData: any) => {
  try {
    // This is a simplified implementation
    // In a real application, you would make an API call to create a user
    console.log('Creating user:', userData);
    return {
      status: 200,
      message: 'User created successfully',
      data: {
        _id: `user-${Date.now()}`,
        ...userData
      }
    };
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// Function to update an existing user
export const updateUser = async (userId: string, userData: any) => {
  try {
    // This is a simplified implementation
    // In a real application, you would make an API call to update a user
    console.log('Updating user:', userId, userData);
    return {
      status: 200,
      message: 'User updated successfully',
      data: {
        _id: userId,
        ...userData
      }
    };
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// Function to delete a user
export const deleteUser = async (userId: string) => {
  try {
    // This is a simplified implementation
    // In a real application, you would make an API call to delete a user
    console.log('Deleting user:', userId);
    return {
      status: 200,
      message: 'User deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Function to create a new company
export const createCompany = async (companyData: any) => {
  try {
    // This is a simplified implementation
    // In a real application, you would make an API call to create a company
    console.log('Creating company:', companyData);
    return {
      status: 200,
      message: 'Company created successfully',
      data: {
        _id: `company-${Date.now()}`,
        ...companyData
      }
    };
  } catch (error) {
    console.error('Error creating company:', error);
    throw error;
  }
};

// Function to create a new department
export const createDepartment = async (departmentData: any) => {
  try {
    // This is a simplified implementation
    // In a real application, you would make an API call to create a department
    console.log('Creating department:', departmentData);
    return {
      status: 200,
      message: 'Department created successfully',
      data: {
        _id: `dept-${Date.now()}`,
        ...departmentData
      }
    };
  } catch (error) {
    console.error('Error creating department:', error);
    throw error;
  }
};

// Function to fetch only active users
export const fetchActiveUsers = async () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/admin/admins`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result.data || result;
  } catch (error) {
    console.error('Error fetching active users:', error);
    throw error;
  }
};

// Function to fetch all users with pagination
export const fetchAllUsersWithPagination = async (page: number, limit: number, search: string = '', userType: string = '') => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    // Build query parameters
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) params.append('search', search);
    if (userType) params.append('userType', userType);
    
    const response = await fetch(`${API_AUTH_URL}/api/admin/admins?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Return data in the expected format for pagination
    return {
      users: result.data || result,
      totalCount: result.totalCount || (result.data ? result.data.length : 0),
      currentPage: page,
      totalPages: Math.ceil((result.totalCount || (result.data ? result.data.length : 0)) / limit)
    };
  } catch (error) {
    console.error('Error fetching users with pagination:', error);
    throw error;
  }
};

// Function to fetch users by department
export const fetchUsersByDepartment = async (departmentId: string) => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/department/${departmentId}/users`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Return the users data
    return {
      users: result.data || result
    };
  } catch (error) {
    console.error('Error fetching users by department:', error);
    throw error;
  }
};

// Function to fetch companies
export const fetchCompanies = async () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/company`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Check if result.data exists and is an array
    let companiesData = [];
    
    if (result.data) {
      // If result.data is an array, use it directly
      if (Array.isArray(result.data)) {
        companiesData = result.data;
      } else {
        // If result.data is a single object, wrap it in an array
        companiesData = [result.data];
      }
    } else if (Array.isArray(result)) {
      // If result itself is an array, use it directly
      companiesData = result;
    } else if (result && typeof result === 'object') {
      // If result is a single object, wrap it in an array
      companiesData = [result];
    }
    
    // Return the companies data
    return {
      companies: companiesData
    };
  } catch (error) {
    console.error('Error fetching companies:', error);
    throw error;
  }
};

// Function to fetch departments
export const fetchDepartments = async () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/department`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Check if result.data exists and is an array
    let departmentsData = [];
    
    if (result.data) {
      // If result.data is an array, use it directly
      if (Array.isArray(result.data)) {
        departmentsData = result.data;
      } else {
        // If result.data is a single object, wrap it in an array
        departmentsData = [result.data];
      }
    } else if (Array.isArray(result)) {
      // If result itself is an array, use it directly
      departmentsData = result;
    } else if (result && typeof result === 'object') {
      // If result is a single object, wrap it in an array
      departmentsData = [result];
    }
    
    // Return the departments data
    return {
      departments: departmentsData
    };
  } catch (error) {
    console.error('Error fetching departments:', error);
    throw error;
  }
};

// Function to create a user using REST API
export const createUserWithREST = async (userData: any) => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/admin/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

// Function to update a user using REST API
export const updateUserWithREST = async (userId: string, userData: any) => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/admin/${userId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// Function to delete a user using REST API
export const deleteUserWithREST = async (userId: string) => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/admin/${userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Function to create a company using REST API
export const createCompanyWithREST = async (companyData: any) => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/company/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(companyData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating company:', error);
    throw error;
  }
};

// Function to create a child company using REST API
export const createChildCompanyWithREST = async (companyData: any) => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/company/create-child`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(companyData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating child company:', error);
    throw error;
  }
};

// Function to update a company using REST API
export const updateCompanyWithREST = async (companyId: string, companyData: any) => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/company/company/${companyId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(companyData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating company:', error);
    throw error;
  }
};

// Function to create a department using REST API
export const createDepartmentWithREST = async (departmentData: any) => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/department/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(departmentData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error creating department:', error);
    throw error;
  }
};

// Function to update a department using REST API
export const updateDepartmentWithREST = async (departmentId: string, departmentData: any) => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/department/${departmentId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(departmentData),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating department:', error);
    throw error;
  }
};

// Function to fetch all companies
export const fetchAllCompanies = async () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/company`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Check if result.data exists and is an array
    let companiesData = [];
    
    if (result.data) {
      // If result.data is an array, use it directly
      if (Array.isArray(result.data)) {
        companiesData = result.data;
      } else {
        // If result.data is a single object, wrap it in an array
        companiesData = [result.data];
      }
    } else if (Array.isArray(result)) {
      // If result itself is an array, use it directly
      companiesData = result;
    } else if (result && typeof result === 'object') {
      // If result is a single object, wrap it in an array
      companiesData = [result];
    }
    
    return { data: companiesData };
  } catch (error) {
    console.error('Error fetching companies:', error);
    throw error;
  }
};

// Function to fetch all departments
export const fetchAllDepartments = async () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_AUTH_URL}/api/department`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    // Check if result.data exists and is an array
    let departmentsData = [];
    
    if (result.data) {
      // If result.data is an array, use it directly
      if (Array.isArray(result.data)) {
        departmentsData = result.data;
      } else {
        // If result.data is a single object, wrap it in an array
        departmentsData = [result.data];
      }
    } else if (Array.isArray(result)) {
      // If result itself is an array, use it directly
      departmentsData = result;
    } else if (result && typeof result === 'object') {
      // If result is a single object, wrap it in an array
      departmentsData = [result];
    }
    
    return { data: departmentsData };
  } catch (error) {
    console.error('Error fetching departments:', error);
    throw error;
  }
};

// Course Service Integration

// GraphQL query to fetch total number of courses and count of each publish status
export const GET_COURSE_COUNTS = gql`
  query MyQuery {
    allCourses(statusCount: true) {
      totalCount
      statusCounts {
        count
        status
      }
    }
  }
`;

// GraphQL query to fetch all active (non-deleted) courses with their complete details
export const GET_ACTIVE_COURSES = gql`
  query MyQuery {
    allCoursesbyPackage(isDeleted: false) {
      courses {
        _id
        createdAt
        createdBy
        creationStage
        deletedAt
        deletedBy
        description
        desktopAvailable
        hls
        isDeleted
        language
        publishStatus
        thumbnail
        title
      }
    }
  }
`;

// Interface for course status counts
export interface CourseStatusCount {
  count: number;
  status: string;
}

// Interface for course counts response
export interface CourseCountsResponse {
  totalCount: number;
  statusCounts: CourseStatusCount[];
}

// Interface for course details
export interface CourseDetail {
  _id: string;
  createdAt: string;
  createdBy: string;
  creationStage: string;
  deletedAt: string | null;
  deletedBy: string | null;
  description: string;
  desktopAvailable: boolean;
  hls: string;
  isDeleted: boolean;
  language: string;
  publishStatus: string;
  thumbnail: string;
  title: string;
}

// Interface for active courses response
export interface ActiveCoursesResponse {
  courses: CourseDetail[];
}

// Function to fetch course counts
export const fetchCourseCounts = async (): Promise<CourseCountsResponse> => {
  try {
    const { data } = await authClient.query({
      query: GET_COURSE_COUNTS,
      fetchPolicy: 'no-cache'
    });
    return data.allCourses;
  } catch (error) {
    console.error('Error fetching course counts:', error);
    throw error;
  }
};

// Function to fetch active courses
export const fetchActiveCourses = async (): Promise<ActiveCoursesResponse> => {
  try {
    const { data } = await authClient.query({
      query: GET_ACTIVE_COURSES,
      fetchPolicy: 'no-cache'
    });
    return data.allCourses;
  } catch (error) {
    console.error('Error fetching active courses:', error);
    throw error;
  }
};

// Package Service Integration

// GraphQL query to fetch total package count and status-wise breakdown
export const GET_PACKAGE_COUNTS = gql`
  query MyQuery {
    getPackageCounts(statusCount: true) {
      totalCount
      statusCounts {
        status
        count
      }
    }
  }
`;

// GraphQL query to fetch all non-deleted packages with detailed information
export const GET_ALL_PACKAGES_DETAILS = gql`
  query MyQuery {
    getPackageCounts(isDeleted: false) {
      packages {
        _id
        bannerUrl
        courseIds
        createdAt
        createdBy
        deletedAt
        deletedBy
        description
        isActive
        isDeleted
        isDraft
        status
        telegramId
        themeUrl
        title
        updatedAt
        updatedBy
      }
    }
  }
`;

// Interface for package status counts
export interface PackageStatusCount {
  status: string;
  count: number;
}

// Interface for package counts response
export interface PackageCountsResponse {
  totalCount: number;
  statusCounts: PackageStatusCount[];
}

// Interface for package details
export interface PackageDetail {
  _id: string;
  bannerUrl: string;
  courseIds: string[];
  createdAt: string;
  createdBy: string;
  deletedAt: string | null;
  deletedBy: string | null;
  description: string;
  isActive: boolean;
  isDeleted: boolean;
  isDraft: boolean;
  status: string;
  telegramId: string[];
  themeUrl: string;
  title: string;
  updatedAt: string;
  updatedBy: string;
}

// Interface for all packages response
export interface AllPackagesResponse {
  packages: PackageDetail[];
}

// Function to fetch package counts
export const fetchPackageCounts = async (): Promise<PackageCountsResponse> => {
  try {
    const { data } = await authClient.query({
      query: GET_PACKAGE_COUNTS,
      fetchPolicy: 'no-cache'
    });
    return data.getPackageCounts;
  } catch (error) {
    console.error('Error fetching package counts:', error);
    throw error;
  }
};

// Function to fetch all package details
export const fetchAllPackageDetails = async (): Promise<AllPackagesResponse> => {
  try {
    const { data } = await authClient.query({
      query: GET_ALL_PACKAGES_DETAILS,
      fetchPolicy: 'no-cache'
    });
    return data.getPackageCounts;
  } catch (error) {
    console.error('Error fetching package details:', error);
    throw error;
  }
};



















